import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import type { BadgeNotificationData } from '../types/gamification.types';
import { RARITY_COLORS } from '../constants/badges';
import { BadgeIcon } from './BadgeIcon';

interface CelebrationOverlayProps {
  data: BadgeNotificationData;
  onDismiss: () => void;
}

const AUTO_CLOSE_MS = 5000;

export function CelebrationOverlay({ data, onDismiss }: CelebrationOverlayProps) {
  const [progress, setProgress] = useState(100);
  const confettiFired = useRef(false);
  const colors = RARITY_COLORS[data.badge.rarity];

  // Fire confetti on mount
  useEffect(() => {
    if (confettiFired.current) return;
    confettiFired.current = true;

    const fireConfetti = () => {
      const defaults = {
        spread: 360,
        ticks: 100,
        gravity: 0.4,
        decay: 0.94,
        startVelocity: 30,
        colors: [colors.primary, colors.secondary, colors.particle, '#ffffff'],
      };

      confetti({ ...defaults, particleCount: 60, origin: { x: 0.5, y: 0.4 } });

      setTimeout(() => {
        confetti({ ...defaults, particleCount: 40, origin: { x: 0.3, y: 0.5 } });
      }, 200);

      setTimeout(() => {
        confetti({ ...defaults, particleCount: 40, origin: { x: 0.7, y: 0.5 } });
      }, 400);
    };

    // Small delay so the overlay is visible first
    setTimeout(fireConfetti, 300);
  }, [colors]);

  // Auto-close timer
  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / AUTO_CLOSE_MS) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        onDismiss();
      }
    }, 50);
    return () => clearInterval(interval);
  }, [onDismiss]);

  return (
    <motion.div
      className="fixed inset-0 z-notification flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      onClick={onDismiss}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />

      {/* Cosmic Particles */}
      <CosmicParticles color={colors.particle} primaryColor={colors.primary} />

      {/* Expanding Rings */}
      <ExpandingRings color={colors.primary} />

      {/* Floating Stars */}
      <FloatingStars color={colors.secondary} />

      {/* Content */}
      <motion.div
        className="relative z-10 text-center px-8"
        initial={{ scale: 0.6, y: 30, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.6, y: 30, opacity: 0 }}
        transition={{ type: 'spring', damping: 15, stiffness: 200, delay: 0.1 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Large Icon */}
        <motion.div
          className="mx-auto w-28 h-28 rounded-full flex items-center justify-center mb-6"
          style={{
            background: `radial-gradient(circle, ${colors.primary}30, transparent)`,
            boxShadow: `0 0 60px ${colors.glow}`,
          }}
          animate={{
            boxShadow: [
              `0 0 40px ${colors.glow}`,
              `0 0 80px ${colors.glow}`,
              `0 0 40px ${colors.glow}`,
            ],
          }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <BadgeIcon
            iconName={data.badge.icon}
            className="w-16 h-16"
            style={{ color: colors.primary }}
          />
        </motion.div>

        {/* Label */}
        <motion.p
          className="text-xs font-bold uppercase tracking-[0.3em] mb-3"
          style={{ color: colors.secondary }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          Accomplissement Débloqué
        </motion.p>

        {/* Title */}
        <motion.h2
          className="text-4xl sm:text-5xl font-bold text-white mb-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, type: 'spring', damping: 20 }}
        >
          {data.badge.name}
        </motion.h2>

        {/* Description */}
        <motion.p
          className="text-lg text-slate-300 max-w-md mx-auto mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          {data.badge.description}
        </motion.p>

        {/* Rarity tag */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.7 }}
        >
          <span
            className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider"
            style={{ backgroundColor: `${colors.primary}25`, color: colors.primary }}
          >
            {data.badge.rarity === 'rare' ? 'Rare' : 'Épique'}
          </span>
        </motion.div>

        {/* Progress bar */}
        <div className="mt-8 mx-auto max-w-[200px] h-1 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: colors.primary, width: `${progress}%` }}
            transition={{ duration: 0.05 }}
          />
        </div>
      </motion.div>
    </motion.div>
  );
}

function CosmicParticles({ color, primaryColor }: { color: string; primaryColor: string }) {
  // Générées une seule fois au montage pour rester pures au re-render
  const [particles] = useState(() =>
    Array.from({ length: 60 }, (_, i) => ({
      angle: (i / 60) * 360,
      distance: 100 + Math.random() * 300,
      size: 1 + Math.random() * 3,
      duration: 2 + Math.random() * 3,
      delay: Math.random() * 0.8,
      useParticleColor: Math.random() > 0.5,
    }))
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(({ angle, distance, size, duration, delay, useParticleColor }, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: size,
            height: size,
            backgroundColor: useParticleColor ? color : primaryColor,
            left: '50%',
            top: '50%',
          }}
          initial={{ x: 0, y: 0, opacity: 0.8, scale: 1 }}
          animate={{
            x: Math.cos((angle * Math.PI) / 180) * distance,
            y: Math.sin((angle * Math.PI) / 180) * distance,
            opacity: 0,
            scale: 0,
          }}
          transition={{ duration, delay, ease: 'easeOut' }}
        />
      ))}
    </div>
  );
}

function ExpandingRings({ color }: { color: string }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      {[0, 0.4, 0.8].map((delay, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full border"
          style={{ borderColor: `${color}40` }}
          initial={{ width: 40, height: 40, opacity: 0.6 }}
          animate={{ width: 400, height: 400, opacity: 0 }}
          transition={{ duration: 2.5, delay, ease: 'easeOut', repeat: Infinity, repeatDelay: 1 }}
        />
      ))}
    </div>
  );
}

function FloatingStars({ color }: { color: string }) {
  // Générées une seule fois au montage pour rester pures au re-render
  const [stars] = useState(() =>
    Array.from({ length: 12 }, () => ({
      x: 10 + Math.random() * 80,
      y: 10 + Math.random() * 80,
      size: 4 + Math.random() * 8,
      duration: 2 + Math.random() * 3,
      delay: Math.random() * 2,
    }))
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {stars.map(({ x, y, size, duration, delay }, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{ left: `${x}%`, top: `${y}%` }}
          animate={{
            y: [0, -20, 0],
            opacity: [0, 1, 0],
            scale: [0.5, 1, 0.5],
          }}
          transition={{
            duration,
            delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </motion.div>
      ))}
    </div>
  );
}
